import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useImpersonation } from '../../../shared/features/session/hooks/use-impersonation';
import { useToast } from '../../../shared/hooks/use-toast';
import { useUserRecord } from './use-user-record';
import { deactivateUser, reactivateUser } from '../users.api';

/**
 * A user's own page: the record plus the account actions that used to sit on every list row.
 * Deactivate/reactivate have no form, so their failures toast (CLAUDE.md → Web conventions).
 */
export function useUserDetail(id: string) {
  const { state, reload } = useUserRecord(id);
  const impersonation = useImpersonation();
  const { state: currentUser } = useCurrentUser();
  const toast = useToast();

  async function changeStatus(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const signedIn = currentUser.status === 'authenticated' ? currentUser.user : undefined;

  return {
    state,
    currentUserId: signedIn?.id,
    hasSecondFactor: Boolean(signedIn?.signedInWithCode || signedIn?.totpEnabled || signedIn?.emailOtpEnabled),
    canImpersonate: signedIn?.permissions.includes('user.impersonate') ?? false,
    actAs: impersonation.start,
    deactivate: (name: string) => changeStatus(() => deactivateUser(id), `${name} deactivated`, `Couldn't deactivate ${name}`),
    reactivate: (name: string) => changeStatus(() => reactivateUser(id), `${name} reactivated`, `Couldn't reactivate ${name}`),
  };
}
